/**
 * Script loaders for GTM, Meta Pixel, TikTok Pixel, Google Ads/GA4 gtag.
 * Loaded once at init; no-ops on subsequent calls.
 *
 * Each loader detects whether the host page has already loaded the same
 * platform global (fbq, ttq, gtag, tlq, GTM container). When the global
 * exists, we skip script injection and piggyback on the existing instance —
 * the fire functions in pixels.ts call the same globals regardless of who
 * loaded them.
 */
const loadedScripts = new Set();
function loadScript(id, src) {
    if (loadedScripts.has(id))
        return;
    if (typeof document === 'undefined')
        return;
    if (document.getElementById(id)) {
        loadedScripts.add(id);
        return;
    }
    const script = document.createElement('script');
    script.id = id;
    script.async = true;
    script.src = src;
    document.head.appendChild(script);
    loadedScripts.add(id);
}
function loadInlineScript(id, code) {
    if (loadedScripts.has(id))
        return;
    if (typeof document === 'undefined')
        return;
    if (document.getElementById(id)) {
        loadedScripts.add(id);
        return;
    }
    const script = document.createElement('script');
    script.id = id;
    script.textContent = code;
    document.head.appendChild(script);
    loadedScripts.add(id);
}
function loadGTMContainers(containerIds) {
    for (const containerId of containerIds) {
        // Skip if this container is already managed by the host page.
        if (typeof window !== 'undefined' && window.google_tag_manager?.[containerId]) {
            loadedScripts.add(`ekho-gtm-${containerId}`);
            continue;
        }
        const scriptId = `ekho-gtm-${containerId}`;
        loadInlineScript(scriptId, `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${containerId}');`);
    }
}
function loadGtagScript(tagId) {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        // gtag already exists on the page — reuse it, don't re-inject the script
        // or re-send config (which would cause a duplicate page view in GA4).
        loadedScripts.add('ekho-gtag');
        return;
    }
    loadScript('ekho-gtag', `https://www.googletagmanager.com/gtag/js?id=${tagId}`);
    if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
        window.gtag = function gtag(...args) {
            window.dataLayer.push(args);
        };
        window.gtag('js', new Date());
        window.gtag('config', tagId);
    }
}
function loadMetaPixel(pixelId) {
    if (typeof window === 'undefined')
        return;
    const id = 'ekho-fbq';
    if (loadedScripts.has(id))
        return;
    loadInlineScript(id, `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${pixelId}');`);
}
function loadTikTokPixel(pixelId) {
    if (typeof window === 'undefined')
        return;
    const id = 'ekho-ttq';
    if (loadedScripts.has(id))
        return;
    // If TikTok pixel is already loaded and this pixel ID is already initialized,
    // skip injection to avoid a duplicate page view and duplicate script fetch.
    const existingTtq = window.ttq;
    if (existingTtq && existingTtq._i?.[pixelId]) {
        loadedScripts.add(id);
        return;
    }
    loadInlineScript(id, `!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{};ttq._i[e]=[];ttq._i[e]._u=r;ttq._t=ttq._t||{};ttq._t[e]=+new Date;ttq._o=ttq._o||{};ttq._o[e]=n||{};var rl=d.createElement("script");rl.type="text/javascript";rl.async=!0;rl.src=r+"?sdkid="+e+"&lib="+t;d.getElementsByTagName("head")[0].appendChild(rl)};ttq.load('${pixelId}');ttq.page();}(window,document,'ttq');`);
}
function loadTracklutionScript(siteId, trackingDomain) {
    if (typeof window === 'undefined')
        return;
    const id = 'ekho-tlq';
    if (loadedScripts.has(id))
        return;
    const domain = trackingDomain ?? 'cdn.tracklution.com';
    const version = Date.now();
    const primaryUrl = `https://${domain}/js/script-dynamic.js?version=${version}`;
    const fallbackUrl = `https://main-62206.trlution.com/js/script-dynamic.js?version=${version}`;
    loadInlineScript(id, `!function(t,l,r,o,c,k,s){if(t.tlq)return;c=t.tlq=function(){c.callMethod?c.callMethod(arguments):c.queue.push(arguments)};if(!t._tlq)t._tlq=c;c.push=c;c.loaded=!0;c.version='1.0';c.src=o;c.queue=[];k=l.createElement(r);k.async=!0;c.pd=false;c.tools=null;k.src=o;s=l.getElementsByTagName(r)[0];s.parentNode.insertBefore(k,s);k.onerror=function(){o='${fallbackUrl}';t._tlq.src=o;k=l.createElement(r);k.async=!0;k.src=o;s.parentNode.insertBefore(k,s)}}(window,document,'script','${primaryUrl}');tlq('init','${siteId}');tlq('track','PageView');`);
}
/**
 * Load gtag and configure both Google Ads + GA4 when both are present.
 * When gtag already existed on the page, `loadGtagScript` is a no-op and
 * we skip the secondary `config` call to avoid a duplicate page view.
 */
function loadGtagFromConfig(config) {
    const gtagTagId = config.googleAds?.tagId ?? config.googleAnalytics?.measurementId;
    if (!gtagTagId)
        return;
    const gtagExisted = typeof window !== 'undefined' && typeof window.gtag === 'function';
    loadGtagScript(gtagTagId);
    // Only send the secondary config if we injected gtag ourselves.
    if (!gtagExisted && config.googleAds?.tagId && config.googleAnalytics?.measurementId) {
        window.gtag?.('config', config.googleAnalytics.measurementId);
    }
}
/**
 * Load all configured tracking scripts. Every configured platform is loaded
 * independently — we don't skip pixels when GTM is present. Dealers may
 * intentionally run both during a GTM migration or use a mix of GTM (for GA4)
 * and direct pixels (for Meta). Dedup is handled by ad platforms via event_id.
 */
function loadMarketingScripts(config) {
    if (config.gtm?.containerIds?.length) {
        loadGTMContainers(config.gtm.containerIds);
    }
    loadGtagFromConfig(config);
    if (config.meta?.pixelId) {
        loadMetaPixel(config.meta.pixelId);
    }
    if (config.tiktok?.pixelId) {
        loadTikTokPixel(config.tiktok.pixelId);
    }
    if (config.tracklution?.siteId) {
        loadTracklutionScript(config.tracklution.siteId, config.tracklution.trackingDomain);
    }
}

export { loadGTMContainers, loadGtagScript, loadMarketingScripts, loadMetaPixel, loadTikTokPixel, loadTracklutionScript };
//# sourceMappingURL=script-loader.js.map
